import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
    Badge,
    Box,
    Button,
    Flex,
    Heading,
    Spinner,
    Stack,
    Text
} from '@chakra-ui/react'
import { FiArrowRight, FiBell } from 'react-icons/fi'
import { Link as RouterLink } from 'react-router-dom'
import { AppNotification, useNotifications } from '@/shared/hooks/useNotifications'
import {
    NOTIFICATION_GROUP_ORDER,
    NOTIFICATION_STYLES
} from '@/components/notificationTheme'

const NotificationRow = ({
    notification,
    colorScheme,
    accent,
    tint,
    onRead
}: {
    notification: AppNotification
    colorScheme: string
    accent: string
    tint: string
    onRead: () => void
}) => (
    <Flex
        align="center"
        gap={3}
        borderWidth="1px"
        borderColor="gray.100"
        borderLeftWidth="3px"
        borderLeftColor={notification.isRead ? 'gray.200' : accent}
        borderRadius="md"
        bg={notification.isRead ? 'white' : tint}
        px={4}
        py={3}
    >
        <Box minW={0} flex="1">
            <Text fontSize="sm" fontWeight="medium" noOfLines={2}>
                {notification.title}
            </Text>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                {notification.description}
            </Text>
        </Box>
        <Button
            as={RouterLink}
            to={notification.href}
            size="xs"
            colorScheme={colorScheme}
            rightIcon={<FiArrowRight />}
            flexShrink={0}
            onClick={onRead}
        >
            Review
        </Button>
    </Flex>
)

/**
 * Dashboard notification feed: pending teacher requests for the admin teacher's
 * branch, and suspected duplicate parent accounts.
 *
 * Grouped by kind and collapsed by default — an admin with a long backlog
 * shouldn't have to scroll past it to reach the rest of the dashboard. Unread
 * counts stay on the closed headers so nothing hides silently.
 */
export default function NotificationsSection() {
    const {
        notifications,
        otherBranchCount,
        unreadCount,
        markRead,
        markAllRead,
        hasFeed,
        loading
    } = useNotifications()

    if (!hasFeed) return null

    const groups = NOTIFICATION_GROUP_ORDER.map((type) => {
        const items = notifications.filter((notification) => notification.type === type)
        return {
            type,
            ...NOTIFICATION_STYLES[type],
            items,
            unread: items.filter((notification) => !notification.isRead).length
        }
    }).filter((group) => group.items.length > 0)

    return (
        <Box mt={6}>
            <Flex align="center" gap={2} mb={3} flexWrap="wrap">
                <Box as={FiBell} aria-hidden />
                <Heading size="sm">Notifications</Heading>
                {unreadCount > 0 ? (
                    <Badge colorScheme="red" borderRadius="full" px={2}>
                        {unreadCount} new
                    </Badge>
                ) : null}
                {loading ? <Spinner size="xs" color="gray.400" /> : null}
                <Box flex="1" />
                {unreadCount > 0 ? (
                    <Button size="xs" variant="link" onClick={markAllRead}>
                        Mark all read
                    </Button>
                ) : null}
            </Flex>

            {groups.length === 0 ? (
                <Box
                    borderWidth="1px"
                    borderStyle="dashed"
                    borderColor="gray.200"
                    borderRadius="md"
                    p={6}
                    textAlign="center"
                >
                    <Text color="gray.500" fontSize="sm">
                        Nothing waiting for you.
                        {otherBranchCount > 0
                            ? ` ${otherBranchCount} awaiting approval in other branches.`
                            : " You're all caught up."}
                    </Text>
                </Box>
            ) : (
                // No `defaultIndex`, so every group starts closed.
                <Accordion allowMultiple>
                    {groups.map((group) => (
                        <AccordionItem
                            key={group.type}
                            borderWidth="1px"
                            borderColor="gray.100"
                            borderRadius="md"
                            mb={2}
                            overflow="hidden"
                        >
                            <AccordionButton
                                py={3}
                                borderLeftWidth="3px"
                                borderLeftColor={
                                    group.unread > 0 ? group.accent : 'gray.200'
                                }
                                _hover={{ bg: 'gray.50' }}
                            >
                                <Flex
                                    align="center"
                                    gap={2}
                                    flex="1"
                                    textAlign="left"
                                    flexWrap="wrap"
                                >
                                    <Box
                                        as={group.icon}
                                        color={group.accent}
                                        aria-hidden
                                    />
                                    <Text fontSize="sm" fontWeight="medium">
                                        {group.label}
                                    </Text>
                                    <Badge borderRadius="full" px={2}>
                                        {group.items.length}
                                    </Badge>
                                    {group.unread > 0 ? (
                                        <Badge
                                            colorScheme="red"
                                            borderRadius="full"
                                            px={2}
                                        >
                                            {group.unread} new
                                        </Badge>
                                    ) : null}
                                </Flex>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel pb={3} pt={0}>
                                <Stack spacing={2}>
                                    {group.items.map((notification) => (
                                        <NotificationRow
                                            key={notification.id}
                                            notification={notification}
                                            colorScheme={group.colorScheme}
                                            accent={group.accent}
                                            tint={group.tint}
                                            onRead={() => markRead(notification.id)}
                                        />
                                    ))}
                                </Stack>
                            </AccordionPanel>
                        </AccordionItem>
                    ))}
                </Accordion>
            )}

            {groups.length > 0 && otherBranchCount > 0 ? (
                <Text fontSize="xs" color="gray.500" mt={2}>
                    {otherBranchCount} more awaiting approval in other branches.
                </Text>
            ) : null}
        </Box>
    )
}
